import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { FirebaseAuthProvider } from './components/auth/FirebaseAuthProvider'
import { OrganizationSecurityProvider } from './contexts/OrganizationSecurityContext'

// Keep lightweight/critical components as static imports
import DashboardLayout from './components/layout/DashboardLayout'
import { AuthenticatedRoute } from './components/auth/AuthenticatedRoute'
import ProtectedRoute from './components/auth/ProtectedRoute'
import OwnerProtectedRoute from './components/auth/OwnerProtectedRoute'

// Lazy-load all page components for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const FormsPage = lazy(() => import('./pages/FormsPage'))
const FormBuilderPage = lazy(() => import('./pages/FormBuilderPage'))
const SubmissionsPage = lazy(() => import('./pages/SubmissionsPageTabbedPaginated'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const OwnersDashboardPage = lazy(() => import('./pages/OwnersDashboardPage'))
const PublicFormPage = lazy(() => import('./pages/PublicFormPage'))
const PublicSuccessPage = lazy(() => import('./pages/PublicSuccessPage'))
const PayPalCallbackPage = lazy(() => import('./pages/PayPalCallbackPage'))
const StripeCallbackPage = lazy(() => import('./pages/StripeCallbackPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const ESignPage = lazy(() => import('./pages/ESignPage'))
const ESignNewPage = lazy(() => import('./pages/ESignNewPage'))
const ESignDetailPage = lazy(() => import('./pages/ESignDetailPage'))
const ESignSignPage = lazy(() => import('./pages/ESignSignPage'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  )
}

function App() {
  // Basic app startup and safe offline services initialization
  useEffect(() => {
    console.log('🚀 [App] Starting application...');
    console.log('🔧 [App] Environment:', import.meta.env.VITE_ENVIRONMENT);
    console.log('🔧 [App] Build time:', new Date().toISOString());
    console.log('✅ [App] Basic app loaded successfully');

    // Initialize offline services with comprehensive error handling
    const initializeOfflineServices = async () => {
      try {
        console.log('🔄 [App] Initializing offline services...');
        
        // Dynamic import to prevent initialization errors from crashing the app
        const { 
          offlineServicesManager,
          serviceWorkerManager 
        } = await import('./services/offlineServicesManager');
        
        // Initialize service worker first (most critical for offline functionality)
        try {
          await serviceWorkerManager.register();
          const status = serviceWorkerManager.getStatus?.();
          if (status?.isRegistered) {
            console.log('✅ [App] Service worker registered successfully');
          } else {
            console.log('⚠️ [App] Service worker not registered (env:', import.meta.env.VITE_ENVIRONMENT, ')');
          }
        } catch (swError) {
          console.warn('⚠️ [App] Service worker registration failed:', swError);
          // Continue initialization - app can work without SW
        }
        
        // Initialize offline services manager
        try {
          await offlineServicesManager.initialize();
          console.log('✅ [App] Offline services manager initialized');
        } catch (osmError) {
          console.warn('⚠️ [App] Offline services manager initialization failed:', osmError);
          // Continue - basic app functionality preserved
        }
        
        // Set global flag to indicate offline services availability
        window.__OFFLINE_SERVICES_AVAILABLE__ = true;
        console.log('✅ [App] Offline services initialization completed');
        
      } catch (criticalError) {
        console.error('❌ [App] Critical error during offline services initialization:', criticalError);
        // Ensure this doesn't crash the app
        window.__OFFLINE_SERVICES_AVAILABLE__ = false;
      }
    };

    // Run initialization after core app is loaded
    setTimeout(() => {
      initializeOfflineServices();
    }, 1000); // Brief delay to ensure Firebase services are initialized first
  }, []);

  return (
    <Router>
      <FirebaseAuthProvider>
        <OrganizationSecurityProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Authenticated Route - Landing for guests, Dashboard for auth users */}
          <Route path="/" element={<AuthenticatedRoute />} />

          {/* Public Routes */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signup-complete" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/contact" element={<ContactPage />} />
          
          {/* PayPal OAuth Callback */}
          <Route path="/paypal/callback" element={<PayPalCallbackPage />} />

          {/* Stripe Connect OAuth Callback */}
          <Route path="/stripe/callback" element={<StripeCallbackPage />} />
          
          
          {/* Legacy signup route - redirect to new flow */}
          <Route path="/signup-old" element={<SignupPage />} />
          
          {/* Protected Core Routes with Layout */}
          <Route path="/dashboard" element={<ProtectedRoute requiresSubscription><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<DashboardPage />} />
            <Route path="*" element={<DashboardPage />} />
          </Route>
          <Route path="/forms" element={<ProtectedRoute requiresSubscription><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<FormsPage />} />
            <Route path="builder/new" element={<FormBuilderPage />} />
            <Route path="builder/:id" element={<FormBuilderPage />} />
            
          </Route>
          <Route path="/submissions" element={<ProtectedRoute requiresSubscription><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<SubmissionsPage />} />
          </Route>
          <Route path="/settings" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<SettingsPage />} />
          </Route>
          <Route path="/billing" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<BillingPage />} />
          </Route>

          {/* E-Sign Routes (protected, with layout) */}
          <Route path="/esign" element={<ProtectedRoute requiresSubscription><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<ESignPage />} />
            <Route path="new" element={<ESignNewPage />} />
            <Route path=":envelopeId" element={<ESignDetailPage />} />
          </Route>

          {/* E-Sign Public Signing Route (no auth) */}
          <Route path="/esign/sign/:envelopeId" element={<ESignSignPage />} />

          {/* Owner-only Routes */}
          <Route path="/owners-dashboard" element={<OwnerProtectedRoute><OwnersDashboardPage /></OwnerProtectedRoute>} />

          {/* Public Form Routes */}
          <Route path="/f/:formId" element={<PublicFormPage />} />
          <Route path="/f/:formId/success" element={<PublicSuccessPage />} />
          
          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </Suspense>
        </OrganizationSecurityProvider>
      </FirebaseAuthProvider>
    </Router>
  )
}

export default App